import React, { useEffect, useState } from 'react';
import { useNavigate } from 'react-router-dom';
import { CheckIcon } from '@heroicons/react/24/solid';
import { useAuth } from '../../contexts/AuthContext';
import { authApi } from '../../services/api';
import { Button } from '../../components/ui/button';
import { Input } from '../../components/ui/input';
import { Label } from '../../components/ui/label';
import { Alert, AlertDescription } from '../../components/ui/alert';
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from '../../components/ui/select';
import { Checkbox } from '../../components/ui/checkbox';
import {
  ORGANIZATION_TYPES,
  CARE_SETTINGS,
  BILLING_TYPES,
  RCM_DEPARTMENTS,
  PAYER_FOCUS,
  INTEREST_AREAS,
  ROLE_LEVELS,
  US_STATES,
  MAC_REGIONS,
} from '../../components/profileConstants';
import type { ProfileFormData, PreferencesFormData } from '../../components/ProfilePreferencesForm';

// ─── Step definitions ────────────────────────────────────────────────────────

const STEPS = [
  { id: 1, label: 'Profile' },
  { id: 2, label: 'Care Setting' },
  { id: 3, label: 'Department' },
  { id: 4, label: 'Interests' },
  { id: 5, label: 'Geography' },
] as const;

const TOTAL_STEPS = STEPS.length;

const EMPTY_PREFS: PreferencesFormData = {
  care_settings: [],
  billing_types: [],
  rcm_departments: [],
  payer_focus: [],
  interest_areas: [],
  role_level: '',
  primary_state: '',
  mac_region: '',
  additional_states: [],
};

// ─── Shared sub-components ───────────────────────────────────────────────────

interface CheckboxGroupProps {
  options: readonly string[];
  selected: string[];
  onChange: (selected: string[]) => void;
  columns?: 1 | 2;
}

function CheckboxGroup({ options, selected, onChange, columns = 2 }: CheckboxGroupProps) {
  const toggle = (option: string) =>
    onChange(
      selected.includes(option)
        ? selected.filter((s) => s !== option)
        : [...selected, option]
    );

  return (
    <div className={`grid gap-2.5 ${columns === 2 ? 'grid-cols-1 sm:grid-cols-2' : 'grid-cols-1'}`}>
      {options.map((option) => (
        <div key={option} className="flex items-start gap-2.5">
          <Checkbox
            id={option}
            checked={selected.includes(option)}
            onCheckedChange={() => toggle(option)}
            className="mt-0.5 shrink-0"
          />
          <label htmlFor={option} className="text-sm leading-snug cursor-pointer select-none">
            {option}
          </label>
        </div>
      ))}
    </div>
  );
}

// ─── Progress bar ─────────────────────────────────────────────────────────────

function StepIndicator({ current }: { current: number }) {
  return (
    <div className="w-full mb-8">
      <div className="flex items-center justify-between">
        {STEPS.map((step, index) => {
          const done = step.id < current;
          const active = step.id === current;
          return (
            <React.Fragment key={step.id}>
              {/* Step circle */}
              <div className="flex flex-col items-center gap-1 min-w-0">
                <div
                  className={`w-8 h-8 rounded-full flex items-center justify-center text-xs font-semibold shrink-0 transition-colors ${
                    done
                      ? 'bg-primary text-primary-foreground'
                      : active
                      ? 'bg-primary text-primary-foreground ring-4 ring-primary/20'
                      : 'bg-muted text-muted-foreground'
                  }`}
                >
                  {done ? <CheckIcon className="w-4 h-4" /> : step.id}
                </div>
                <span
                  className={`text-[10px] font-medium hidden sm:block ${
                    active ? 'text-foreground' : 'text-muted-foreground'
                  }`}
                >
                  {step.label}
                </span>
              </div>
              {/* Connector */}
              {index < STEPS.length - 1 && (
                <div
                  className={`flex-1 h-0.5 mx-2 transition-colors ${
                    done ? 'bg-primary' : 'bg-border'
                  }`}
                />
              )}
            </React.Fragment>
          );
        })}
      </div>
    </div>
  );
}

// ─── Main component ───────────────────────────────────────────────────────────

const WelcomePage: React.FC = () => {
  const { user, refreshAuth } = useAuth();
  const navigate = useNavigate();

  const [step, setStep] = useState(1);
  const [profile, setProfile] = useState<ProfileFormData>({
    full_name: '',
    organization: '',
    organization_type: '',
    title: '',
  });
  const [prefs, setPrefs] = useState<PreferencesFormData>(EMPTY_PREFS);
  const [saving, setSaving] = useState(false);
  const [error, setError] = useState<string | null>(null);

  // Redirect if profile already completed
  useEffect(() => {
    if (user?.profile_completed) {
      navigate('/ask', { replace: true });
    }
  }, [user, navigate]);

  // Pre-fill from existing user data
  useEffect(() => {
    if (user) {
      setProfile({
        full_name: user.full_name ?? '',
        organization: user.organization ?? '',
        organization_type: user.organization_type ?? '',
        title: user.title ?? '',
      });
    }
  }, [user]);

  // Pre-fill any existing preferences
  useEffect(() => {
    authApi.getPreferences().then((p) => {
      setPrefs({
        care_settings: p.care_settings ?? [],
        billing_types: p.billing_types ?? [],
        rcm_departments: p.rcm_departments ?? [],
        payer_focus: p.payer_focus ?? [],
        interest_areas: p.interest_areas ?? [],
        role_level: p.role_level ?? '',
        primary_state: p.primary_state ?? '',
        mac_region: p.mac_region ?? '',
        additional_states: p.additional_states ?? [],
      });
    }).catch(() => {/* use defaults */});
  }, []);

  const updateProfile = (field: keyof ProfileFormData, value: string) =>
    setProfile((p) => ({ ...p, [field]: value }));

  const updatePrefs = (field: keyof PreferencesFormData, value: string | string[]) =>
    setPrefs((p) => ({ ...p, [field]: value }));

  const toggleList = (field: keyof PreferencesFormData, option: string) => {
    const current = prefs[field] as string[];
    updatePrefs(
      field,
      current.includes(option) ? current.filter((s) => s !== option) : [...current, option]
    );
  };

  const handleFinish = async () => {
    setError(null);
    setSaving(true);
    try {
      await authApi.updateProfile({
        full_name: profile.full_name || undefined,
        organization: profile.organization || undefined,
        organization_type: profile.organization_type || undefined,
        title: profile.title || undefined,
        profile_completed: true,
      });
      await authApi.updatePreferences({
        care_settings: prefs.care_settings,
        billing_types: prefs.billing_types,
        rcm_departments: prefs.rcm_departments,
        payer_focus: prefs.payer_focus,
        interest_areas: prefs.interest_areas,
        role_level: prefs.role_level || undefined,
        primary_state: prefs.primary_state || undefined,
        mac_region: prefs.mac_region || undefined,
        additional_states: prefs.additional_states,
      });
      await refreshAuth();
      navigate('/ask', { replace: true });
    } catch (err: unknown) {
      const detail =
        err && typeof err === 'object' && 'response' in err
          ? (err as { response?: { data?: { detail?: string } } }).response?.data?.detail
          : 'Failed to save profile';
      setError(Array.isArray(detail) ? detail.join(', ') : String(detail));
    } finally {
      setSaving(false);
    }
  };

  // ── Step content ────────────────────────────────────────────────────────────

  const renderStep = () => {
    switch (step) {
      case 1:
        return (
          <div className="space-y-5">
            <div>
              <h2 className="text-xl font-semibold">Tell us about yourself</h2>
              <p className="text-sm text-muted-foreground mt-1">
                This helps personalize your experience and connect you with the right content.
              </p>
            </div>

            <div className="grid grid-cols-1 sm:grid-cols-2 gap-4">
              <div className="space-y-2">
                <Label htmlFor="fullName">Name</Label>
                <Input
                  id="fullName"
                  value={profile.full_name}
                  onChange={(e) => updateProfile('full_name', e.target.value)}
                  placeholder="Your full name"
                  className="h-10"
                />
              </div>
              <div className="space-y-2">
                <Label htmlFor="title">
                  Title{' '}
                  <span className="text-muted-foreground font-normal text-xs">(optional)</span>
                </Label>
                <Input
                  id="title"
                  value={profile.title}
                  onChange={(e) => updateProfile('title', e.target.value)}
                  placeholder="e.g. Director of Revenue Cycle"
                  className="h-10"
                />
              </div>
            </div>

            <div className="grid grid-cols-1 sm:grid-cols-2 gap-4">
              <div className="space-y-2">
                <Label htmlFor="organization">Company / Organization</Label>
                <Input
                  id="organization"
                  value={profile.organization}
                  onChange={(e) => updateProfile('organization', e.target.value)}
                  placeholder="Your organization"
                  className="h-10"
                />
              </div>
              <div className="space-y-2">
                <Label htmlFor="orgType">Organization Type</Label>
                <Select
                  value={profile.organization_type}
                  onValueChange={(v) => updateProfile('organization_type', v)}
                >
                  <SelectTrigger id="orgType" className="h-10">
                    <SelectValue placeholder="Select type" />
                  </SelectTrigger>
                  <SelectContent>
                    {ORGANIZATION_TYPES.map((t) => (
                      <SelectItem key={t} value={t}>
                        {t}
                      </SelectItem>
                    ))}
                  </SelectContent>
                </Select>
              </div>
            </div>
          </div>
        );

      case 2:
        return (
          <div className="space-y-7">
            <div>
              <h2 className="text-xl font-semibold">Care setting & billing</h2>
              <p className="text-sm text-muted-foreground mt-1">
                Select all that apply to your work or areas of interest.
              </p>
            </div>

            <div className="space-y-3">
              <h3 className="text-sm font-semibold">Care Setting</h3>
              <CheckboxGroup
                options={CARE_SETTINGS}
                selected={prefs.care_settings}
                onChange={(v) => updatePrefs('care_settings', v)}
              />
            </div>

            <div className="space-y-3">
              <h3 className="text-sm font-semibold">Billing Type</h3>
              <CheckboxGroup
                options={BILLING_TYPES}
                selected={prefs.billing_types}
                onChange={(v) => updatePrefs('billing_types', v)}
                columns={1}
              />
            </div>
          </div>
        );

      case 3:
        return (
          <div className="space-y-7">
            <div>
              <h2 className="text-xl font-semibold">Department & payer focus</h2>
              <p className="text-sm text-muted-foreground mt-1">
                Select all that apply to your role and the payers you work with.
              </p>
            </div>

            <div className="space-y-3">
              <h3 className="text-sm font-semibold">Revenue Cycle Department</h3>
              <CheckboxGroup
                options={RCM_DEPARTMENTS}
                selected={prefs.rcm_departments}
                onChange={(v) => updatePrefs('rcm_departments', v)}
              />
            </div>

            <div className="space-y-3">
              <h3 className="text-sm font-semibold">Payer Focus</h3>
              <CheckboxGroup
                options={PAYER_FOCUS}
                selected={prefs.payer_focus}
                onChange={(v) => updatePrefs('payer_focus', v)}
              />
            </div>
          </div>
        );

      case 4:
        return (
          <div className="space-y-7">
            <div>
              <h2 className="text-xl font-semibold">Interests & role</h2>
              <p className="text-sm text-muted-foreground mt-1">
                Used to surface the most relevant content, alerts, and policy updates for you.
              </p>
            </div>

            <div className="space-y-3">
              <h3 className="text-sm font-semibold">Interest Areas</h3>
              <CheckboxGroup
                options={INTEREST_AREAS}
                selected={prefs.interest_areas}
                onChange={(v) => updatePrefs('interest_areas', v)}
              />
            </div>

            <div className="space-y-3">
              <h3 className="text-sm font-semibold">Role Level</h3>
              <Select
                value={prefs.role_level}
                onValueChange={(v) => updatePrefs('role_level', v)}
              >
                <SelectTrigger className="h-10 w-full sm:w-56">
                  <SelectValue placeholder="Select your level" />
                </SelectTrigger>
                <SelectContent>
                  {ROLE_LEVELS.map((r) => (
                    <SelectItem key={r} value={r}>
                      {r}
                    </SelectItem>
                  ))}
                </SelectContent>
              </Select>
            </div>
          </div>
        );

      case 5:
        return (
          <div className="space-y-7">
            <div>
              <h2 className="text-xl font-semibold">Geography</h2>
              <p className="text-sm text-muted-foreground mt-1">
                Determines the regional policy updates, payer rules, and regulatory content shown
                to you. Many reimbursement policies vary by state and MAC region.
              </p>
            </div>

            <div className="grid grid-cols-1 sm:grid-cols-2 gap-4">
              <div className="space-y-2">
                <Label htmlFor="primaryState">Primary State</Label>
                <Select
                  value={prefs.primary_state}
                  onValueChange={(v) => updatePrefs('primary_state', v)}
                >
                  <SelectTrigger id="primaryState" className="h-10">
                    <SelectValue placeholder="Select primary state" />
                  </SelectTrigger>
                  <SelectContent>
                    {US_STATES.map((s) => (
                      <SelectItem key={s} value={s}>
                        {s}
                      </SelectItem>
                    ))}
                  </SelectContent>
                </Select>
              </div>

              <div className="space-y-2">
                <Label htmlFor="macRegion">MAC Region</Label>
                <Select
                  value={prefs.mac_region}
                  onValueChange={(v) => updatePrefs('mac_region', v)}
                >
                  <SelectTrigger id="macRegion" className="h-10">
                    <SelectValue placeholder="Select MAC region" />
                  </SelectTrigger>
                  <SelectContent>
                    {MAC_REGIONS.map((r) => (
                      <SelectItem key={r} value={r}>
                        {r}
                      </SelectItem>
                    ))}
                  </SelectContent>
                </Select>
              </div>
            </div>

            <div className="space-y-3">
              <div>
                <h3 className="text-sm font-semibold">Additional States</h3>
                <p className="text-xs text-muted-foreground mt-0.5">
                  Select any additional states you operate in or want to monitor.
                </p>
              </div>
              <div className="grid grid-cols-2 sm:grid-cols-3 gap-2 max-h-60 overflow-y-auto border rounded-md p-3">
                {US_STATES.map((state) => (
                  <div key={state} className="flex items-center gap-2">
                    <Checkbox
                      id={`as-${state}`}
                      checked={prefs.additional_states.includes(state)}
                      onCheckedChange={() => toggleList('additional_states', state)}
                      className="shrink-0"
                    />
                    <label
                      htmlFor={`as-${state}`}
                      className="text-sm leading-snug cursor-pointer select-none"
                    >
                      {state}
                    </label>
                  </div>
                ))}
              </div>
            </div>
          </div>
        );

      default:
        return null;
    }
  };

  // ── Render ───────────────────────────────────────────────────────────────────

  return (
    <div className="min-h-screen bg-background flex flex-col">
      {/* Slim top bar */}
      <div className="border-b px-6 py-3 flex items-center justify-between">
        <span className="text-sm font-semibold">RCM Library</span>
        <button
          type="button"
          onClick={() => navigate('/ask', { replace: true })}
          className="text-xs text-muted-foreground hover:text-foreground transition-colors"
        >
          Skip for now
        </button>
      </div>

      <div className="flex-1 flex items-start justify-center px-4 py-10">
        <div className="w-full max-w-xl">
          {/* Welcome header — only on step 1 */}
          {step === 1 && (
            <div className="mb-6">
              <h1 className="text-2xl font-semibold">Welcome to RCM Library</h1>
              <p className="text-sm text-muted-foreground mt-1">
                Let's set up your profile in a few quick steps. You can update these at any time
                in Settings.
              </p>
            </div>
          )}

          <StepIndicator current={step} />

          {/* Step content card */}
          <div className="rounded-xl border bg-card p-6 shadow-sm">
            {renderStep()}

            {error && (
              <Alert variant="destructive" className="mt-6">
                <AlertDescription>{error}</AlertDescription>
              </Alert>
            )}

            {/* Navigation */}
            <div className="flex items-center justify-between mt-8 pt-4 border-t">
              <Button
                type="button"
                variant="ghost"
                onClick={() => setStep((s) => s - 1)}
                disabled={step === 1}
              >
                Back
              </Button>

              <span className="text-xs text-muted-foreground">
                Step {step} of {TOTAL_STEPS}
              </span>

              {step < TOTAL_STEPS ? (
                <Button type="button" onClick={() => setStep((s) => s + 1)}>
                  Continue
                </Button>
              ) : (
                <Button type="button" onClick={handleFinish} disabled={saving}>
                  {saving ? 'Saving…' : 'Complete setup'}
                </Button>
              )}
            </div>
          </div>
        </div>
      </div>
    </div>
  );
};

export default WelcomePage;
